import { Button, Modal } from 'react-bootstrap'; // Import Modal
import { useDispatch, useSelector } from 'react-redux';
import {
    bringForward,
    sendBackward,
    pageDeleted,
    itemMoved
} from 'Vendor/Store/studio';
import { AppDispatch, RootState } from 'Vendor/Store/store';
import { redo, undo } from 'Vendor/Store/undoable';
import { generateHtml } from 'Vendor/Utils/HtmlGenerator';
import { useEffect, useState } from 'react';
import _ExportModal from './_ExportModal';
import { saveCreation } from 'Vendor/Store/studio/thunk';
import {
    selectSingleSelectedItem,
    selectAssets,
    selectLayouts,
    selectGroupSelectedItems,
} from 'Vendor/Store/studio/selectors';
import { IconType, selectActiveTool, setActiveTool } from 'Vendor/Store/studio/activeToolSlice';
import { AlignBottomIcon, AlignCenterIcon, AlignLeftIcon, AlignMiddleIcon, AlignRightIcon, AlignTopIcon } from './AlignmentIcon';
import { AlignmentParams, GRID_INDEX_OFFSET } from 'Vendor/Utils/StudioTypes';
import Api from 'Vendor/Api';

interface TopControlsProps {
    creationId: number;
}

const TopControls: React.FC<TopControlsProps> = ({ creationId }) => {
    const activeTool = useSelector(selectActiveTool)
    const dispatch = useDispatch<AppDispatch>();
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportHtmlContent, setExportHtmlContent] = useState('');
    const [exportJsonContent, setExportJsonContent] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const canUndo = useSelector((state: RootState) => state.studio.past.length > 0);
    const canRedo = useSelector((state: RootState) => state.studio.future.length > 0);
    const saveStatus = useSelector((state: RootState) => state.studio.present.status);
    const isSaving = saveStatus === 'loading';
    const hasChanges = canUndo;

    const groupBounds = useSelector((state: RootState) => state.studioGrid.groupBounds);
    const gridMetrics = useSelector((state: RootState) => state.studioGrid.gridMetrics);
    const isGroupActive = !!groupBounds;

    const isActive = (iconType: IconType) =>
        activeTool.itemId === selectedItem?.id && activeTool.iconType === iconType

    const handleUndo = () => {
        if (canUndo) {
            dispatch(undo());
        }
    };

    const handleRedo = () => {
        if (canRedo) {
            dispatch(redo());
        }
    };

    const selectedItem = useSelector(selectSingleSelectedItem);
    const selectedGroupItems = useSelector(selectGroupSelectedItems)
    const activePageId = useSelector(
        (state: RootState) => state.studio.present.activePageId,
    );
    const pageIds = useSelector(
        (state: RootState) => state.studio.present.pageIds,
    );
    const canDeletePage = pageIds.length > 1;

    const handleBringForward = () => {
        if (selectedItem && activePageId) {
            dispatch(
                bringForward({ itemId: selectedItem.id, pageId: activePageId }),
            )
        }
    };

    const handleSendBackward = () => {
        if (selectedItem && activePageId) {
            dispatch(
                sendBackward({ itemId: selectedItem.id, pageId: activePageId }),
            )
        }
    };

    const assets = useSelector(selectAssets);
    const layouts = useSelector(selectLayouts);

    const handleExportClick = () => {
        if (!assets || !layouts) {
            alert("Could not export: Design data not found.");

            return;
        }

        const jsonContent = JSON.stringify({ assets, layouts }, null, 2);
        setExportJsonContent(jsonContent);

        setExportHtmlContent("");
        setShowExportModal(true);

        Api.get(`/studio/creation/${creationId}/preview`, {
            responseType: "text",
            transformResponse: [(data) => data],
        }).then((response) => {
            setExportHtmlContent(response.data);
        })
        .catch((err) => {
            console.error("Failed to fetch preview HTML:", err);

            setExportHtmlContent("");
        });
    };

    const handleSave = () => {
        dispatch(saveCreation());
    };

    const openDeleteConfirm = () => setShowDeleteConfirm(true);
    const closeDeleteConfirm = () => setShowDeleteConfirm(false);

    const handleDeletePage = () => {
        if (activePageId && canDeletePage) {
            dispatch(pageDeleted({ pageId: activePageId }));
        }
        closeDeleteConfirm();
    };

    const handleToolClick = (itemId: string, itemType: string, iconType: IconType) => {
        dispatch(setActiveTool({ itemId, itemType, iconType }))
    }

    const handleAlignmentClick = (params: AlignmentParams) => {
        const { direction, alignment } = params;

        if (
            !selectedGroupItems?.length ||
            !groupBounds ||
            !gridMetrics
        )
            return;

        const { colWidth, rowHeight } = gridMetrics;

        const groupColStart =
            Math.round(groupBounds.left / colWidth) + GRID_INDEX_OFFSET;
        const groupRowStart =
            Math.round(groupBounds.top / rowHeight) + GRID_INDEX_OFFSET;

        const groupCols =
            Math.round(groupBounds.width / colWidth);
        const groupRows =
            Math.round(groupBounds.height / rowHeight);

        selectedGroupItems.forEach((item) => {
            let {
                position: { col_start, row_start },
                size: { col_span, row_span },
            } = item;

            if (direction === "HORIZONTAL") {
                if (alignment === "left")
                    col_start = groupColStart;

                if (alignment === "center")
                    col_start =
                        groupColStart +
                        Math.floor((groupCols - col_span) / 2);

                if (alignment === "right")
                    col_start =
                        groupColStart + (groupCols - col_span);
            }

            if (direction === "VERTICAL") {
                if (alignment === "top")
                    row_start = groupRowStart;

                if (alignment === "middle")
                    row_start =
                        groupRowStart +
                        Math.floor((groupRows - row_span) / 2);

                if (alignment === "bottom")
                    row_start =
                        groupRowStart + (groupRows - row_span);
            }

            dispatch(itemMoved({
                pageId: activePageId,
                itemId: item.id,
                newPosition: {
                    col_start,
                    row_start
                }
            }))
        });
    };

    const renderMenuForSelectedItem = () => {

        /* ------ GROUP MODE ------ */
        if (selectedGroupItems.length > 1) {
            return (
                <>
                    {/* Fill Color */}
                    <div
                        className={`fixed-box cursor-pointer ${isActive("backgroundColor") ? 'border-active' : 'border-inactive'}`}
                        title="Background Color"
                        onClick={() => handleToolClick('group', 'GROUP', "backgroundColor")}
                    />

                    <div className="divider"></div>

                    {isGroupActive && (
                        <>
                            {/* Vertical alignment */}
                            <div
                                className="fixed-box cursor-pointer"
                                title="Align Top"
                                onClick={() => handleAlignmentClick({ direction: "VERTICAL", alignment: "top" })}
                            >
                                <AlignTopIcon />
                            </div>

                            <div
                                className="fixed-box cursor-pointer"
                                title="Align Middle"
                                onClick={() => handleAlignmentClick({ direction: "VERTICAL", alignment: "middle" })}
                            >
                                <AlignMiddleIcon />
                            </div>

                            <div
                                className="fixed-box cursor-pointer"
                                title="Align Bottom"
                                onClick={() => handleAlignmentClick({ direction: "VERTICAL", alignment: "bottom" })}
                            >
                                <AlignBottomIcon />
                            </div>

                            <div className="divider"></div>

                            {/* Horizontal alignment */}
                            <div
                                className="fixed-box cursor-pointer"
                                title="Align Left"
                                onClick={() => handleAlignmentClick({ direction: "HORIZONTAL", alignment: "left" })}
                            >
                                <AlignLeftIcon />
                            </div>

                            <div
                                className="fixed-box cursor-pointer"
                                title="Align Center"
                                onClick={() => handleAlignmentClick({ direction: "HORIZONTAL", alignment: "center" })}
                            >
                                <AlignCenterIcon />
                            </div>

                            <div
                                className="fixed-box cursor-pointer"
                                title="Align Right"
                                onClick={() => handleAlignmentClick({ direction: "HORIZONTAL", alignment: "right" })}
                            >
                                <AlignRightIcon />
                            </div>
                            <div className="divider"></div>
                        </>
                    )}

                </>
            );
        }

        if (!selectedItem) {
            return;
        }

        const { id, type, styles, content } = selectedItem;

        switch (type) {
            case "TEXT":
                return (
                    <>
                        {/* Fill Color */}
                        <div
                            className={`fixed-box cursor-pointer ${isActive("backgroundColor") ? 'border-active' : 'border-inactive'}`}
                            title="Background Color"
                            onClick={() => handleToolClick(id, type, "backgroundColor")}
                            style={{ backgroundColor: styles.backgroundColor || 'transparent' }}
                        />

                        {/* Text Icon */}
                        <div
                            className="fixed-box text-icon cursor-pointer"
                            title="Add Font"
                            onClick={() => handleToolClick(id, type, "text")}
                        >
                            <span>T</span>
                            <span>T</span>
                        </div>

                        <div className="divider"></div>
                    </>
                );

            case "EXTERNAL_IMAGE":
            case "PRODUCT":
            case "FILE":
                return (
                    <>
                        {/* Image Icon */}
                        <img
                            src={content.src || ""}
                            alt="Selected"
                            className={`fixed-box cursor-pointer object-fit-contain ${isActive("image") ? 'border-active' : 'border-inactive'}`}
                            onClick={() => handleToolClick(id, type, "image")}
                        />

                        {/* Fill Color */}
                        <div
                            className={`fixed-box cursor-pointer ${isActive("backgroundColor") ? 'border-active' : 'border-inactive'}`}
                            title="Background color"
                            onClick={() => handleToolClick(id, type, "backgroundColor")}
                            style={{ backgroundColor: styles.backgroundColor || 'transparent' }}
                        />

                        {/* Border Color */}
                        <div
                            className="fixed-box cursor-pointer bg-white border-outline"
                            title="Outline color"
                            onClick={() => handleToolClick(id, type, "outlineColor")}
                            style={{ 
                                border: `2px solid ${styles.outlineColor || "#000"}`
                            }}
                        />

                        <div className="divider"></div>
                    </>
                );

            case "COLOR":
                return (
                    <>
                        {/* Fill Color */}
                        <div
                            className={`fixed-box cursor-pointer ${isActive("backgroundColor") ? 'border-active' : 'border-inactive'}`}
                            title="Background color"
                            onClick={() => handleToolClick(id, type, "backgroundColor")}
                            style={{ backgroundColor: styles.backgroundColor || 'transparent' }}
                        />

                        <div className="divider"></div>
                    </>
                );

            default:
                return;
        }
    };

    useEffect(() => {
        if (selectedItem?.type === 'COLOR') {
            dispatch(setActiveTool({ itemId: selectedItem.id, itemType: 'COLOR', iconType: 'backgroundColor' }));
        }
    }, [selectedItem?.id, selectedItem?.type, dispatch]);

    const isItemSelected = selectedItem !== null;

    return (
        <>
            <div className="top-controls d-flex justify-content-between align-items-center position-absolute top-2 w-100">
                <div className="bg-white p-2 rounded-2 space-x-4">
                    <i
                        className={`fa-solid fa-redo fs-2 ${!canRedo ? 'text-muted' : 'text-black'}`}
                        onClick={handleRedo}
                        style={{ cursor: canRedo ? 'pointer' : 'not-allowed' }}
                        title="Redo"
                    />

                    <i
                        className={`fa-solid fa-undo fs-2 ${!canUndo ? 'text-muted' : 'text-black'}`}
                        onClick={handleUndo}
                        style={{ cursor: canUndo ? 'pointer' : 'not-allowed' }}
                        title="Undo"
                    />
                </div>
                <div className="w-25 flex-1 d-flex justify-content-center align-items-center space-x-4 bg-white middle-control p-2 rounded centered-controls">

                    {renderMenuForSelectedItem()}

                    <i
                        className={`fa-solid fa-layer-group fa-flip-vertical fs-2 ${isItemSelected ? 'cursor-pointer' : 'text-muted'}`}
                        onClick={handleSendBackward}
                        title="Send Backward"
                        style={{ transform: 'rotate(180deg)' }}
                    ></i>

                    <i
                        className={`fa-solid fa-layer-group fs-2 ${isItemSelected ? 'cursor-pointer' : 'text-muted'}`}
                        onClick={handleBringForward}
                        title="Bring Forward"
                    ></i>
                    <Button
                        className={`fs-5 rounded ${isSaving ? 'btn-primary pulsing' : hasChanges ? 'btn-success' : 'btn-secondary'}`}
                        onClick={handleSave}
                        disabled={isSaving || !hasChanges}
                        title="Save Changes"
                    >
                        <i
                            className={`fa-solid ${isSaving ? 'fa-spinner fa-spin' : 'fa-save'}`}
                        ></i>
                    </Button>
                    <Button
                        variant="danger"
                        className="fs-5 rounded"
                        onClick={openDeleteConfirm}
                        title="Delete Current Page"
                        disabled={!canDeletePage}
                    >
                        <i className="fa-solid fa-trash-alt"></i>
                    </Button>
                </div>
                <div className="d-flex gap-2">
                    <Button
                        className="fs-5 rounded"
                        onClick={handleExportClick}
                        title="Preview & Export"
                    >
                        <i className="fa-solid fa-eye me-2 py-2"></i>Preview
                    </Button>
                </div>
            </div>

            <_ExportModal
                show={showExportModal}
                onHide={() => setShowExportModal(false)}
                htmlContent={exportHtmlContent}
                jsonContent={exportJsonContent}
            />

            <Modal show={showDeleteConfirm} onHide={closeDeleteConfirm} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Page</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete this page? This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeDeleteConfirm}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDeletePage}>
                        Delete Page
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default TopControls;
